import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, ChevronRight, ArrowLeft, User, Clock, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Comprehensive blog posts data
const allBlogPosts = [
  {
    id: 1,
    title: 'How to Save Money While Shopping Online',
    excerpt: 'Discover the best strategies for finding deals and coupons online. Learn insider tips that can save you hundreds of dollars every month.',
    content: `
      <p>Online shopping has revolutionized the way we buy products, but it's also opened up countless opportunities to save money. Here are the top strategies to maximize your savings:</p>
      
      <h2>1. Use Browser Extensions</h2>
      <p>Install browser extensions like Honey, Rakuten, or Capital One Shopping. These tools automatically search for and apply coupon codes at checkout, ensuring you never miss a discount.</p>
      
      <h2>2. Sign Up for Email Newsletters</h2>
      <p>Most retailers offer 10-20% off your first purchase when you sign up for their newsletter. Create a dedicated email for shopping to keep your inbox organized.</p>
      
      <h2>3. Wait for Sales Events</h2>
      <p>Major sales events like Black Friday, Cyber Monday, Prime Day, and end-of-season sales offer the deepest discounts. Plan your purchases around these dates.</p>
      
      <h2>4. Use Cashback Programs</h2>
      <p>Stack your savings by using cashback websites and credit cards. Rakuten, TopCashback, and many credit cards offer 1-10% back on purchases.</p>
      
      <h2>5. Compare Prices</h2>
      <p>Never buy from the first site you visit. Use price comparison tools like Google Shopping or CamelCamelCamel to track price history and find the best deals.</p>
    `,
    date: '2025-01-10',
    author: 'Sarah Johnson',
    readTime: '5 min read',
    category: 'Tips & Tricks',
    image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=400&fit=crop'
  },
  {
    id: 2,
    title: 'Top 10 Coupon Sites You Should Know',
    excerpt: 'A comprehensive list of the best coupon websites for 2025. These sites can help you find discounts on everything from groceries to electronics.',
    content: `
      <p>Finding the right coupon site can make a huge difference in your savings. Here are the top 10 coupon sites every smart shopper should bookmark:</p>
      
      <h2>1. Rakuten (formerly Ebates)</h2>
      <p>Earn cashback on purchases from over 2,500 stores. Rakuten consistently offers the best cashback rates in the industry.</p>
      
      <h2>2. RetailMeNot</h2>
      <p>One of the oldest and most trusted coupon sites with millions of verified codes across thousands of retailers.</p>
      
      <h2>3. Honey</h2>
      <p>This browser extension automatically finds and applies coupon codes at checkout. It also tracks price drops on products you're watching.</p>
      
      <h2>4. Slickdeals</h2>
      <p>A community-driven deals site where users share and vote on the best discounts. Great for finding limited-time flash sales.</p>
      
      <h2>5. Coupons.com</h2>
      <p>Perfect for grocery shoppers with printable coupons and digital offers that link to your store loyalty cards.</p>
      
      <h2>6. Brad's Deals</h2>
      <p>Hand-picked deals from trusted brands with a focus on quality over quantity.</p>
      
      <h2>7. DealNews</h2>
      <p>Expert editors curate the best deals from across the internet, with helpful buying advice.</p>
      
      <h2>8. Groupon</h2>
      <p>Great for local deals on restaurants, activities, and services. Also offers product deals and travel discounts.</p>
      
      <h2>9. Amazon Coupon Page</h2>
      <p>Often overlooked, Amazon's coupon page offers clickable coupons on thousands of products.</p>
      
      <h2>10. Coldwater Coupons Store</h2>
      <p>Of course, our site! We curate the best deals with verified cashback offers from top retailers.</p>
    `,
    date: '2025-01-08',
    author: 'Mike Chen',
    readTime: '7 min read',
    category: 'Guides',
    image: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=400&fit=crop'
  },
  {
    id: 3,
    title: '5 Ways to Maximize Your Cashback Rewards',
    excerpt: 'Learn how to get the most out of your online shopping with these cashback tips. Stack rewards and earn more on every purchase.',
    content: `
      <p>Cashback rewards can add up to significant savings over time. Here's how to maximize your earnings:</p>
      
      <h2>1. Stack Multiple Cashback Sources</h2>
      <p>Use a cashback credit card combined with a cashback portal like Rakuten. You can earn cashback from both sources on the same purchase.</p>
      
      <h2>2. Check Multiple Portals</h2>
      <p>Different cashback sites offer different rates. Before making a purchase, compare rates on Rakuten, TopCashback, and Mr. Rebates.</p>
      
      <h2>3. Time Your Purchases</h2>
      <p>Cashback rates often increase during holiday sales. Wait for elevated rates during Black Friday, Cyber Monday, or special promotions.</p>
      
      <h2>4. Don't Forget In-Store Offers</h2>
      <p>Many cashback apps like Ibotta and Checkout 51 offer cashback on in-store purchases too. Link your cards to earn automatically.</p>
      
      <h2>5. Refer Friends</h2>
      <p>Most cashback programs offer referral bonuses. Share your link with friends and family to earn bonus cashback.</p>
    `,
    date: '2025-01-05',
    author: 'Emily Davis',
    readTime: '4 min read',
    category: 'Tips & Tricks',
    image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=800&h=400&fit=crop'
  },
  {
    id: 4,
    title: 'Best Deals for January 2025',
    excerpt: 'Our handpicked selection of the hottest deals this month. From electronics to fashion, find the best January sales.',
    content: `
      <p>January is one of the best months for shopping deals. Retailers are clearing out holiday inventory and offering deep discounts. Here are the categories with the best deals:</p>
      
      <h2>Electronics</h2>
      <p>Post-holiday electronics sales are in full swing. Look for deals on TVs, laptops, and smart home devices as retailers clear inventory.</p>
      
      <h2>Winter Clothing</h2>
      <p>End-of-season winter clothing sales offer 50-70% off coats, boots, and cold-weather accessories.</p>
      
      <h2>Fitness Equipment</h2>
      <p>New Year's resolutions drive fitness sales. Expect discounts on gym equipment, fitness trackers, and workout gear.</p>
      
      <h2>Furniture</h2>
      <p>White sales on bedding and mattresses are a January tradition. Many furniture stores also offer clearance pricing.</p>
      
      <h2>Top Picks This Week</h2>
      <ul>
        <li>Apple AirPods Pro - 15% off at Amazon</li>
        <li>Samsung 65" 4K TV - $300 off at Best Buy</li>
        <li>Alpinestars GP Tech v3 Jacket - 25% off</li>
        <li>Dyson Vacuum - $150 off at Target</li>
      </ul>
    `,
    date: '2025-01-02',
    author: 'Alex Thompson',
    readTime: '6 min read',
    category: 'Deals',
    image: 'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=800&h=400&fit=crop'
  },
  {
    id: 5,
    title: 'How to Use Coupon Codes Effectively',
    excerpt: 'Master the art of using coupon codes to save money on every purchase. Tips for finding and applying codes correctly.',
    content: `
      <p>Coupon codes are one of the easiest ways to save money online. Here's how to use them effectively:</p>
      
      <h2>Where to Find Coupon Codes</h2>
      <p>Check retailer newsletters, social media pages, and dedicated coupon sites. Browser extensions like Honey can automatically find codes.</p>
      
      <h2>Always Try Multiple Codes</h2>
      <p>If one code doesn't work, try another. Many retailers have multiple active promotions at any given time.</p>
      
      <h2>Check for Exclusions</h2>
      <p>Read the fine print. Many codes exclude certain brands, sale items, or have minimum purchase requirements.</p>
      
      <h2>Stack When Possible</h2>
      <p>Some retailers allow stacking a percentage-off code with a free shipping code. Always try combining offers.</p>
      
      <h2>Don't Abandon Your Cart</h2>
      <p>If you don't find a code, leave items in your cart. Many retailers will email you a discount code within 24-48 hours.</p>
    `,
    date: '2024-12-28',
    author: 'Jessica Lee',
    readTime: '5 min read',
    category: 'Guides',
    image: 'https://images.unsplash.com/photo-1556742031-c6961e8560b0?w=800&h=400&fit=crop'
  },
  {
    id: 6,
    title: 'Understanding Cashback Programs',
    excerpt: 'Everything you need to know about cashback programs and how they work. A complete guide for beginners.',
    content: `
      <p>Cashback programs reward you for shopping through their platform. Here's how they work:</p>
      
      <h2>How Cashback Works</h2>
      <p>When you shop through a cashback site, the retailer pays the site a commission. The cashback site shares a portion of this commission with you.</p>
      
      <h2>Types of Cashback</h2>
      <ul>
        <li><strong>Shopping Portals:</strong> Sites like Rakuten and TopCashback</li>
        <li><strong>Credit Card Rewards:</strong> Cards that offer 1-5% back on purchases</li>
        <li><strong>Store-Specific:</strong> Target Circle, Amazon cashback, etc.</li>
        <li><strong>Receipt Scanning:</strong> Apps like Ibotta and Fetch Rewards</li>
      </ul>
      
      <h2>Getting Paid</h2>
      <p>Most programs pay quarterly via PayPal, check, or gift card. Minimum payout thresholds typically range from $1 to $25.</p>
      
      <h2>Tips for Success</h2>
      <p>Always start your shopping session from the cashback portal. Disable ad blockers and clear cookies if tracking issues occur.</p>
    `,
    date: '2024-12-25',
    author: 'David Wilson',
    readTime: '6 min read',
    category: 'Guides',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&h=400&fit=crop'
  },
  {
    id: 7,
    title: 'Black Friday vs Cyber Monday: When to Shop',
    excerpt: 'Wondering when to find the best deals? We compare Black Friday and Cyber Monday to help you plan your shopping strategy.',
    content: `
      <p>Both shopping holidays offer incredible deals, but knowing when to buy what can maximize your savings.</p>
      
      <h2>Black Friday Strengths</h2>
      <ul>
        <li>Best for TVs, appliances, and in-store doorbusters</li>
        <li>Often features limited-quantity deals</li>
        <li>Great for trying products in person before buying</li>
      </ul>
      
      <h2>Cyber Monday Strengths</h2>
      <ul>
        <li>Better for tech, software, and online services</li>
        <li>More time to compare prices</li>
        <li>Often matches or beats Black Friday prices</li>
      </ul>
      
      <h2>Our Recommendation</h2>
      <p>Start watching prices two weeks before Black Friday. Use price tracking tools to see if "deals" are actually good. Don't feel pressured to buy on Black Friday itself—many deals extend through Cyber Monday.</p>
    `,
    date: '2024-12-20',
    author: 'Sarah Johnson',
    readTime: '5 min read',
    category: 'Guides',
    image: 'https://images.unsplash.com/photo-1607083206968-13611e3d76db?w=800&h=400&fit=crop'
  },
  {
    id: 8,
    title: 'Student Discounts You Might Be Missing',
    excerpt: 'College students can save big with these often-overlooked discounts. From software to streaming services.',
    content: `
      <p>Being a student comes with perks beyond education. Here are valuable discounts you might not know about:</p>
      
      <h2>Tech & Software</h2>
      <ul>
        <li>Apple: Education pricing on Macs, iPads, and accessories</li>
        <li>Microsoft: Free Office 365 with .edu email</li>
        <li>Adobe: 60% off Creative Cloud</li>
        <li>Spotify Premium: $5.99/month (includes Hulu)</li>
      </ul>
      
      <h2>Shopping</h2>
      <ul>
        <li>Amazon Prime Student: 50% off membership</li>
        <li>ASOS: 10% student discount</li>
        <li>Nike: 10% off with UNiDAYS</li>
      </ul>
      
      <h2>How to Verify</h2>
      <p>Most discounts require verification through UNiDAYS, Student Beans, or your .edu email address. Keep your student ID handy for in-store discounts.</p>
    `,
    date: '2024-12-15',
    author: 'Emily Davis',
    readTime: '4 min read',
    category: 'Tips & Tricks',
    image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=800&h=400&fit=crop'
  }
];

const BlogPage = () => {
  const { id } = useParams();
  
  // If we have an ID, show single blog post
  if (id) {
    const post = allBlogPosts.find(p => p.id === parseInt(id));
    
    if (!post) {
      return (
        <div className="min-h-screen flex flex-col bg-gray-50">
          <Header />
          <main className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-800 mb-4">Post Not Found</h1>
              <Link to="/blog" className="text-[#345a83] hover:underline">
                ← Back to Blog
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      );
    }

    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        
        <main className="flex-1">
          {/* Breadcrumb */}
          <div className="bg-white border-b">
            <div className="max-w-4xl mx-auto px-4 py-3">
              <div className="flex items-center text-sm text-gray-600">
                <Link to="/" className="hover:text-[#345a83]">Home</Link>
                <ChevronRight size={14} className="mx-2" />
                <Link to="/blog" className="hover:text-[#345a83]">Blog</Link>
                <ChevronRight size={14} className="mx-2" />
                <span className="text-gray-800 font-medium truncate max-w-[200px]">{post.title}</span>
              </div>
            </div>
          </div>

          <article className="max-w-4xl mx-auto px-4 py-8">
            {/* Back Button */}
            <Link 
              to="/blog" 
              className="inline-flex items-center text-[#345a83] hover:underline mb-6"
            >
              <ArrowLeft size={18} className="mr-1" />
              Back to Blog
            </Link>

            {/* Article Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="bg-[#345a83]/10 text-[#345a83] text-xs font-medium px-3 py-1 rounded-full">
                  {post.category}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-gray-500 text-sm">
                <span className="flex items-center gap-1">
                  <User size={14} />
                  {post.author}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={14} />
                  {new Date(post.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                <span className="flex items-center gap-1">
                  <Clock size={14} />
                  {post.readTime}
                </span>
              </div>
            </header>

            {/* Featured Image */}
            <div className="mb-8 rounded-lg overflow-hidden">
              <img 
                src={post.image} 
                alt={post.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>

            {/* Article Content */}
            <div 
              className="prose prose-lg max-w-none prose-headings:text-gray-800 prose-p:text-gray-600 prose-a:text-[#345a83] prose-li:text-gray-600"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* Related Posts */}
            <div className="mt-12 pt-8 border-t">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Related Posts</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {allBlogPosts
                  .filter(p => p.id !== post.id && p.category === post.category)
                  .slice(0, 2)
                  .map(relatedPost => (
                    <Link 
                      key={relatedPost.id}
                      to={`/blog/${relatedPost.id}`}
                      className="bg-white rounded-lg border hover:shadow-md transition-shadow p-4"
                    >
                      <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-500 text-sm">{relatedPost.readTime}</p>
                    </Link>
                  ))}
              </div>
            </div>
          </article>
        </main>

        <Footer />
      </div>
    );
  }

  // Blog listing page
  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <main className="flex-1">
        {/* Page Header */}
        <div className="bg-white border-b mb-6">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Blog</h1>
            <p className="text-gray-600 mt-2">Tips, guides, and news about saving money</p>
          </div>
        </div>

        {/* Blog Posts Grid */}
        <div className="max-w-7xl mx-auto px-4 pb-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allBlogPosts.map((post) => (
              <article
                key={post.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
                data-testid={`blog-post-${post.id}`}
              >
                {/* Post Image */}
                <div className="h-48 overflow-hidden">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                  />
                </div>
                
                <div className="p-6">
                  {/* Category Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="bg-[#345a83]/10 text-[#345a83] text-xs font-medium px-2 py-0.5 rounded">
                      {post.category}
                    </span>
                    <span className="text-gray-400 text-xs flex items-center gap-1">
                      <Clock size={12} />
                      {post.readTime}
                    </span>
                  </div>

                  {/* Date */}
                  <div className="flex items-center text-gray-500 text-sm mb-3">
                    <Calendar size={14} className="mr-1" />
                    {new Date(post.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </div>

                  {/* Title */}
                  <h2 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 hover:text-[#345a83] transition-colors">
                    <Link to={`/blog/${post.id}`}>
                      {post.title}
                    </Link>
                  </h2>

                  {/* Excerpt */}
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>

                  {/* Author & Read More */}
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-xs flex items-center gap-1">
                      <User size={12} />
                      {post.author}
                    </span>
                    <Link
                      to={`/blog/${post.id}`}
                      className="inline-flex items-center text-[#345a83] text-sm font-medium hover:underline"
                      data-testid={`read-more-${post.id}`}
                    >
                      Read more
                      <ChevronRight size={16} className="ml-1" />
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPage;
